import {
  ACC_RECONCILATION,
  ACC_SETUP,
  BILLS_TO_ENTER,
  ACC_TYPE,
  COA,
  COST_CENTER,
  CREDIT_VOUCHER,
  DASHBOARD,
  DEBIT_VOUCHER,
  FINANCIAL_YEAR,
  GL_JOURNAL,
  GL_MAP,
  HELP,
  VOUCHER_APPROVAL_WORKFLOW,
  JOURNAL_VOUCHER,
  MY_REQUESTS,
  OPENING_BALANCE,
  PENDING_APPROVALS,
  PO_TRACKER,
  REPORT_ACCOUNTS_LEDGER,
  REPORT_BALANCE_SHEET,
  REPORT_CASH_BANK_BOOK,
  REPORT_INCOME_STATEMENT,
  REPORT_MANAGER,
  REPORT_TRIAL_BALANCE,
  REPORT_VOUCHER_TXN,
  SUPPLIER_BILLS,
} from '@constants/internal-route.constant'
import {usePermissions} from '@services/helper/permission.helper'
import {SidebarMenuItem} from './SidebarMenuItem'
import {SidebarMenuItemWithSub} from './SidebarMenuItemWithSub'
import {
  APPROVAL_ROUTES,
  APPROVALS_GROUP_ROUTES,
  APPROVALS_GROUP_URLS,
  BANK_GL_ROUTES,
  PAYABLES_ROUTES,
  REPORT_ROUTES,
  SETTINGS_ROUTES,
  VOUCHER_ROUTES,
} from './menu-routes'

// The whole ams navigation: Dashboard, then five groups, then Help. Group children are `gated`
// (hidden without the feature grant: exact equality between the route constant and
// auth.features.url), each group is hidden when none of its children survive (menu-routes.ts),
// and a group's pages are listed A–Z by their label (SidebarMenuItemWithSub).
//
// A group's `to` is only its open/closed key; the screens do not share one url prefix per group,
// so a group is active by its children's urls (`anyOf`). Vouchers starts open: it is where the
// day's work is.
//
// Approvals is stock's group: My Requests (the vouchers I prepared) and Pending Approvals (one inbox
// for every voucher type). Neither has a feature url of its own: My Requests shows to whoever can
// raise a voucher, Pending Approvals to whoever can approve one, and the group is active on its
// own two screens (`childUrls`).
//
// Notifications has no entry of its own: the header bell is the standard placement.
export const SidebarMenuMain = () => {
  const {hasAny} = usePermissions()
  return (
    <>
      <SidebarMenuItem to={DASHBOARD} title='Dashboard' fontIcon='bi-columns-gap' />

      <SidebarMenuItemWithSub
        to='ams-vouchers'
        title='Vouchers'
        fontIcon='bi-receipt'
        anyOf={VOUCHER_ROUTES}
        defaultOpen
      >
        <SidebarMenuItem
          gated
          fontIcon='bi-arrow-down-left-circle'
          to={CREDIT_VOUCHER}
          title='Credit (Receive) Voucher'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-arrow-up-right-circle'
          to={DEBIT_VOUCHER}
          title='Debit (Payment) Voucher'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-journal-text'
          to={JOURNAL_VOUCHER}
          title='Journal (TXN) Voucher'
        />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='ams-approvals'
        title='Approvals'
        fontIcon='bi-check2-square'
        anyOf={APPROVALS_GROUP_ROUTES}
        childUrls={APPROVALS_GROUP_URLS}
      >
        {hasAny(VOUCHER_ROUTES) && (
          <SidebarMenuItem to={MY_REQUESTS} title='My Requests' fontIcon='bi-send' />
        )}
        {hasAny(APPROVAL_ROUTES) && (
          <SidebarMenuItem to={PENDING_APPROVALS} title='Pending Approvals' fontIcon='bi-inbox' />
        )}
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='ams-payables'
        title='Payables'
        fontIcon='bi-truck'
        anyOf={PAYABLES_ROUTES}
        childUrls={[BILLS_TO_ENTER, SUPPLIER_BILLS, PO_TRACKER]}
      >
        {/* not a feature url of its own: whoever enters supplier bills sees what is waiting for one */}
        {hasAny([SUPPLIER_BILLS]) && (
          <SidebarMenuItem to={BILLS_TO_ENTER} title='Bills to Enter' fontIcon='bi-inbox' />
        )}
        <SidebarMenuItem gated fontIcon='bi-receipt-cutoff' to={SUPPLIER_BILLS} title='Supplier Bills' />
        <SidebarMenuItem gated fontIcon='bi-diagram-3' to={PO_TRACKER} title='PO Tracker' />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='ams-bank-gl'
        title='Bank & GL'
        fontIcon='bi-bank'
        anyOf={BANK_GL_ROUTES}
      >
        <SidebarMenuItem
          gated
          fontIcon='bi-journal-bookmark-fill'
          to={ACC_RECONCILATION}
          title='Account Reconciliation'
        />
        <SidebarMenuItem gated fontIcon='bi-arrow-left-right' to={GL_JOURNAL} title='GL Journal' />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='ams-reports'
        title='Reports'
        fontIcon='bi-bar-chart-line'
        anyOf={REPORT_ROUTES}
      >
        <SidebarMenuItem
          gated
          fontIcon='bi-graph-up-arrow'
          to={`${REPORT_MANAGER}?report=COA`}
          title='Reports Manager'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-calculator'
          to={REPORT_TRIAL_BALANCE}
          title='Trial Balance'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-clipboard-data'
          to={REPORT_BALANCE_SHEET}
          title='Balance Sheet'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-graph-up'
          to={REPORT_INCOME_STATEMENT}
          title='Income Statement'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-book'
          to={REPORT_ACCOUNTS_LEDGER}
          title='Accounts Ledger'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-cash-stack'
          to={REPORT_CASH_BANK_BOOK}
          title='Cash/Bank Book'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-receipt-cutoff'
          to={REPORT_VOUCHER_TXN}
          title='Voucher Transaction'
        />
      </SidebarMenuItemWithSub>

      <SidebarMenuItemWithSub
        to='ams-settings'
        title='Settings'
        fontIcon='bi-sliders'
        anyOf={SETTINGS_ROUTES}
      >
        <SidebarMenuItem gated fontIcon='bi-gear' to={ACC_SETUP} title='Accounting Setup' />
        <SidebarMenuItem gated fontIcon='bi-diagram-2' to={COA} title='Chart of Accounts' />
        <SidebarMenuItem gated fontIcon='bi-tags' to={ACC_TYPE} title='Account Type' />
        <SidebarMenuItem gated fontIcon='bi-calendar3' to={FINANCIAL_YEAR} title='Financial Year' />
        <SidebarMenuItem
          gated
          fontIcon='bi-list-columns'
          to={OPENING_BALANCE}
          title='Opening Balance'
        />
        <SidebarMenuItem
          gated
          fontIcon='bi-filter-circle'
          to={COST_CENTER}
          title='Party / Cost Center'
        />
        <SidebarMenuItem gated fontIcon='bi-shuffle' to={GL_MAP} title='GL Account Map' />
        <SidebarMenuItem
          gated
          fontIcon='bi-diagram-3'
          to={VOUCHER_APPROVAL_WORKFLOW}
          title='Approval Workflow'
        />
      </SidebarMenuItemWithSub>

      <SidebarMenuItem to={HELP} title='Help' fontIcon='bi-question-circle' />
    </>
  )
}
